import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { processedTenantsState } from '../store/common-atoms';

// Reads from the shared tenants atom that the tenant selector populates on
// startup. No extra fetch — TenantDisplay must never trigger a dedicated
// lookup; the atom only refreshes when the all-tenants call runs.
//
// Uses the grouped `tenants` list (not `rawTenants`) so duplicate-name
// disambiguation (the ` (1)`, ` (2)` … suffix added by groupCustomers) is
// applied identically to the tenant selector. Falls back to rawTenants only
// while the grouped list is still empty.
const useTenantsMap = () => {
  const { tenants, rawTenants, removedById, isLoading } = useAtomValue(processedTenantsState);
  const source = (tenants?.length ? tenants : rawTenants) || [];
  const byId = useMemo(() => {
    const map = new Map();
    // Seed with removed tenants first (live entries override) so names evicted mid-session still resolve.
    Object.values(removedById || {}).forEach(c => {
      const id = c._id || c.id;
      if (id) map.set(id, c);
    });
    source.forEach(c => {
      const id = c._id || c.id;
      if (id) map.set(id, c);
    });
    return map;
  }, [source, removedById]);
  return { byId, isLoading };
};

export const useTenantDisplay = customerId => {
  const { byId, isLoading } = useTenantsMap();
  const tenant = customerId ? byId.get(customerId) : null;
  return { tenant: tenant || null, name: tenant?.name || customerId || '', isLoading };
};

export const useTenantDisplays = ids => {
  const { byId, isLoading } = useTenantsMap();
  const map = useMemo(() => {
    const out = new Map();
    (ids || []).forEach(id => {
      if (id) out.set(id, byId.get(id) || null);
    });
    return out;
  }, [ids, byId]);
  return { map, isLoading };
};
